import React from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { Button } from 'components/ui'
import { ROUTES } from 'constants/routes.constant'

// useNavigate is a hook and ErrorBoundary must stay a class component (error
// boundaries require getDerivedStateFromError/componentDidCatch, which have no
// hook equivalent), so the navigation is isolated in this small function
// component instead of nesting a Link's <a> inside a Button's <button>.
const GoToDashboardButton = () => {
    const navigate = useNavigate()
    return (
        <Button variant="default" onClick={() => navigate(ROUTES.DASHBOARD)}>
            Go to dashboard
        </Button>
    )
}

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props)
        this.state = { hasError: false }
    }

    static getDerivedStateFromError() {
        return { hasError: true }
    }

    componentDidCatch(error, errorInfo) {
        // eslint-disable-next-line no-console
        console.error('ErrorBoundary caught an error:', error, errorInfo)
    }

    componentDidUpdate(prevProps) {
        // Only reset out of the error state once navigation actually happened
        // while we're showing the fallback UI. Keyed on react-router's
        // location.key (not pathname): key is regenerated by the history lib
        // on every push/replace, including query-string-only or hash-only
        // navigations on the same pathname (e.g. crashing on
        // /dashboard?clubId=5 and hitting "Go to dashboard", which lands back
        // on /dashboard with a different search - pathname alone would never
        // change there and the fallback would stay stuck). This still avoids
        // remounting (and losing state in) every subtree on every nav, since
        // it only resets while hasError is true.
        if (this.state.hasError && prevProps.locationKey !== this.props.locationKey) {
            this.setState({ hasError: false })
        }
    }

    handleReload = () => {
        window.location.reload()
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="flex flex-col items-center justify-center h-[60vh] gap-4 text-center px-4">
                    <h4 className="font-bold">Something went wrong loading this page.</h4>
                    <p className="text-gray-500">
                        Please try reloading, or go back to the dashboard.
                    </p>
                    <div className="flex gap-2">
                        <Button variant="solid" onClick={this.handleReload}>
                            Reload page
                        </Button>
                        <GoToDashboardButton />
                    </div>
                </div>
            )
        }

        return this.props.children
    }
}

ErrorBoundary.propTypes = {
    children: PropTypes.node,
    locationKey: PropTypes.string,
}

export default ErrorBoundary
